import React, { createContext, forwardRef, useContext, useId, useMemo } from "react";
import { Global, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import * as RechartsPrimitive from "recharts";
import { ColorProp, getColorFromTheme } from "styles/themes";

// File adapted from shadcn https://ui.shadcn.com/docs/components/chart
// Adapated to use styled-components instead of tailwindcss

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: ColorProp;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tooltipFormatter?: (value: any) => React.ReactNode; // Rechart uses any for data, can't really be more specific
  };
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = createContext<ChartContextProps | null>(null);

function useChart() {
  const context = useContext(ChartContext);

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return context;
}

const ChartContainer = forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    aspectRatio?: string;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"];
  }
>(({ id, className, children, config, aspectRatio = "16 / 9", ...props }, ref) => {
  const uniqueId = useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <ChartContainerStyled
        data-chart={chartId}
        ref={ref}
        className={className}
        aspectRatio={aspectRatio}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </ChartContainerStyled>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = "Chart";

const ChartContainerStyled = styled.div<{ aspectRatio: string }>(({ theme, aspectRatio }) => ({
  display: "flex",
  aspectRatio,
  justifyContent: "center",
  fontSize: "0.75rem",
  lineHeight: "1rem",

  "& .recharts-cartesian-axis-tick_text": {
    color: theme.colors.sysTextPlaceholder, // fill-muted-foreground
  },
  "& .recharts-cartesian-grid_line[stroke='#ccc']": {
    stroke: `${theme.colors.sysBorderDecorative}80`, // stroke-border/50 (80 = 50% opacity in hex)
  },
  "& .recharts-curve.recharts-tooltip-cursor": {
    stroke: theme.colors.sysBorderDecorative, // stroke-border
  },
  "& .recharts-dot[stroke='#fff']": {
    stroke: "transparent",
  },
  "& .recharts-layer": {
    outline: "2px solid transparent",
    outlineOffset: "2px",
  },
  "& .recharts-polar-grid [stroke='#ccc']": {
    stroke: theme.colors.sysBorderDecorative, // stroke-border
  },
  "& .recharts-radial-bar-background-sector": {
    fill: theme.colors.sysBackgroundAlternative, // fill-muted
  },
  "& .recharts-rectangle.recharts-tooltip-cursor": {
    fill: theme.colors.sysBackgroundAlternative, // fill-muted
  },
  "& .recharts-reference-line [stroke='#ccc']": {
    stroke: theme.colors.sysBorderDecorative, // stroke-border
  },
  "& .recharts-sector[stroke='#fff']": {
    stroke: "transparent",
  },
  "& .recharts-sector": {
    outline: "2px solid transparent",
    outlineOffset: "2px",
  },
  "& .recharts-surface": {
    outline: "2px solid transparent",
    outlineOffset: "2px",
  },
}));

/**
 * Create a CSS variable for each item within the chart config
 * This is so within the chart you don't need to get the color from the theme
 * and pass it down. Instead you use that CSS variable which is the item key
 *
 * e.g. foo: { label: "Unrelated", color: "red" } -> var(--color-foo)
 *
 * Each also gets a `-soft` variable: the same colour mixed into `sysBackgroundDefault`,
 * for a fill that sits under a full-strength line without competing with it. Mixed
 * rather than made transparent, so gridlines don't show through a band. It is opaque,
 * so it only looks right on that background; a chart on a tinted card would need its own.
 */
const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const theme = useTheme();

  const colorConfig = Object.entries(config).map(([key, itemConfig], i) => ({
    key,
    color: itemConfig.color || `sysChart${i + 1}`,
  }));

  return (
    <Global
      styles={{
        [`[data-chart=${id}]`]: Object.assign(
          {},
          ...colorConfig.map(({ key, color }) => {
            const resolved = getColorFromTheme(color, theme);
            return {
              [`--color-${key}`]: resolved,
              [`--color-${key}-soft`]: `color-mix(in srgb, ${resolved} ${SOFT_FILL_PERCENT}%, ${theme.colors.sysBackgroundDefault})`,
            };
          }),
        ),
      }}
    />
  );
};

/** How much of the series colour survives the mix into the page behind it. */
const SOFT_FILL_PERCENT = 25;

const ChartTooltip = RechartsPrimitive.Tooltip;

type TooltipIndicator = "line" | "dot" | "dashed";

const ChartTooltipContent = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
    React.ComponentProps<"div"> & {
      hideLabel?: boolean;
      hideIndicator?: boolean;
      indicator?: TooltipIndicator;
      nameKey?: string;
      labelKey?: string;
      extraRows?: string[]; // Extra rows to show in the tooltip
    }
>(
  (
    {
      active,
      payload: payloadOriginal,
      className,
      indicator = "dot",
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
      extraRows,
      itemSorter,
    },
    ref,
  ) => {
    const { config } = useChart();

    const payload = useMemo(() => {
      if (!payloadOriginal?.length) {
        return payloadOriginal;
      }

      const payloadExtraRows = (extraRows ?? []).map((row) => ({
        ...payloadOriginal[0],
        dataKey: row,
        name: row,
        value: payloadOriginal[0].payload[row],
        fill: `var(--color-${row})`,
        color: `var(--color-${row})`,
      }));

      const rows = [...payloadOriginal, ...payloadExtraRows];

      // recharts declares itemSorter on Tooltip but only honours it in its own
      // default content, so custom content has to apply it itself. Ascending,
      // matching recharts - negate the key for descending.
      if (!itemSorter) {
        return rows;
      }

      return rows.sort((a, b) => {
        const left = itemSorter(a);
        const right = itemSorter(b);
        if (left < right) {
          return -1;
        }
        if (left > right) {
          return 1;
        }
        return 0;
      });
    }, [payloadOriginal, extraRows, itemSorter]);

    const tooltipLabel = useMemo(() => {
      if (hideLabel || !payload?.length) {
        return null;
      }

      const [item] = payload;
      const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        !labelKey && typeof label === "string"
          ? config[label as keyof typeof config]?.label || label
          : itemConfig?.label;

      if (labelFormatter) {
        return (
          <ChartTooltipLabelNested className={labelClassName}>
            {labelFormatter(value, payload)}
          </ChartTooltipLabelNested>
        );
      }

      if (!value) {
        return null;
      }

      return <ChartTooltipLabelNested>{value}</ChartTooltipLabelNested>;
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

    if (!active || !payload?.length) {
      return null;
    }

    const nestLabel = payload.length === 1 && indicator !== "dot";

    return (
      <ChartTooltipWrapper ref={ref} className={className}>
        {!nestLabel ? tooltipLabel : null}
        <ChartTooltipWrapperInner>
          {payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || "value"}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color || item.payload.fill || item.color;

            return (
              <ChartTooltipElement key={item.dataKey} indicator={indicator}>
                {
                  // Use the formatter or fallback to the default rendering
                  formatter && item?.value !== undefined && item.name ? (
                    formatter(item.value, item.name, item, index, item.payload)
                  ) : (
                    <>
                      {itemConfig?.icon ? (
                        <itemConfig.icon />
                      ) : (
                        !hideIndicator && (
                          <ChartTooltipIndicator
                            indicatorColor={indicatorColor}
                            indicator={indicator}
                            nestLabel={nestLabel}
                          />
                        )
                      )}
                      <ChartTooltipContents nestLabel={nestLabel}>
                        <ChartTooltipLabelContainer>
                          {nestLabel ? tooltipLabel : null}
                          <ChartTooltipLabelUnnested>
                            {itemConfig?.label || item.name}
                          </ChartTooltipLabelUnnested>
                        </ChartTooltipLabelContainer>
                        {/* Checked against null, not truthiness, so a legitimate
                            0 still renders. */}
                        {item.value != null && (
                          <ChartTooltipValue>
                            {itemConfig?.tooltipFormatter
                              ? itemConfig.tooltipFormatter(item.value)
                              : item.value.toLocaleString()}
                          </ChartTooltipValue>
                        )}
                      </ChartTooltipContents>
                    </>
                  )
                }
              </ChartTooltipElement>
            );
          })}
        </ChartTooltipWrapperInner>
      </ChartTooltipWrapper>
    );
  },
);
ChartTooltipContent.displayName = "ChartTooltip";

const ChartTooltipLabelNested = styled.div({
  fontWeight: "500",
});

const ChartTooltipWrapper = styled.div(({ theme }) => ({
  display: "grid",
  minWidth: "8rem",
  alignItems: "flex-start",
  gap: "0.375rem",
  borderRadius: "0.5rem",
  borderWidth: "1px",
  borderColor: `${theme.colors.sysBorderDecorative}80`, // stroke-border/50 (80 = 50% opacity in hex)
  backgroundColor: theme.colors.sysBackgroundDefault, // background
  padding: "0.375rem 0.625rem",
  fontSize: "0.75rem",
  lineHeight: "1rem",
  boxShadow: "0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.1)",
}));

// gap is inherited so a consumer capping or loosening the tooltip through
// styled(ChartTooltipContent) moves the rows with it, not just the outer grid.
const ChartTooltipWrapperInner = styled.div({
  display: "grid",
  gap: "inherit",
});

const ChartTooltipElement = styled.div<{ indicator: TooltipIndicator }>(({ theme, indicator }) => ({
  display: "flex",
  width: "100%",
  flexWrap: "wrap",
  gap: "0.5rem",

  ...(indicator === "dot"
    ? {
        alignItems: "center",
      }
    : {
        alignItems: "stretch",
      }),

  "& > svg": {
    height: "0.625rem",
    width: "0.625rem",
    color: theme.colors.sysTextPlaceholder, // text-muted-foreground
  },
}));

const ChartTooltipIndicator = styled.div<{
  indicatorColor: string;
  indicator: TooltipIndicator;
  nestLabel: boolean;
}>(({ indicatorColor, indicator, nestLabel }) => ({
  flexShrink: 0,
  borderRadius: "2px",
  borderColor: indicatorColor,
  backgroundColor: indicatorColor,

  ...(indicator === "dot" && {
    height: "0.625rem",
    width: "0.625rem",
  }),
  ...(indicator === "line" && {
    width: "0.25rem",
  }),
  ...(indicator === "dashed" && {
    width: "0px",
    borderWidth: "1.5px",
    borderStyle: "dashed",
    backgroundColor: "transparent",
    ...(nestLabel && {
      marginTop: "0.125rem",
      marginBottom: "0.125rem",
    }),
  }),
}));

const ChartTooltipContents = styled.div<{ nestLabel: boolean }>(({ nestLabel }) => ({
  display: "flex",
  flex: "1",
  gap: 8,
  justifyContent: "space-between",
  lineHeight: 1,
  alignItems: nestLabel ? "flex-end" : "center",
}));

const ChartTooltipLabelContainer = styled.div({
  display: "grid",
  gap: "0.375rem",
  // Grid items default to min-width auto, which floors the label at its full
  // width and makes the ellipsis below unreachable.
  minWidth: 0,
});

const ChartTooltipLabelUnnested = styled.span(({ theme }) => ({
  color: theme.colors.sysTextPlaceholder, // text-muted-foreground
  // Only bites once something caps the tooltip width. Truncates the series name
  // rather than the value, which is the part people read.
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ChartTooltipValue = styled.span(({ theme }) => ({
  // fontFamily: monospace font
  fontWeight: "500",
  fontVariantNumeric: "tabular-nums",
  color: theme.colors.sysTextDefault, // text-foreground
}));

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> &
    Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign" | "align"> & {
      hideIcon?: boolean;
      nameKey?: string;
      /** Cap each label and ellipsis the overflow, so long names don't force extra rows. */
      maxLabelWidth?: number | string;
      /**
       * Turns each entry into a button that toggles its series. Spread
       * `useLegendToggle`'s `legendProps` rather than wiring these by hand, and give
       * the series the `hide` prop its `seriesProps` returns, or nothing dims on
       * click.
       *
       * Cartesian series only: pie and donut charts hide per `Cell`, not per series.
       */
      hiddenSeries?: ReadonlySet<string>;
      onToggleSeries?: (key: string) => void;
    }
>(
  (
    {
      className,
      hideIcon = false,
      payload,
      verticalAlign = "bottom",
      align,
      nameKey,
      maxLabelWidth,
      hiddenSeries,
      onToggleSeries,
    },
    ref,
  ) => {
    const { config } = useChart();
    const theme = useTheme();

    if (!payload?.length) {
      return null;
    }

    return (
      <ChartLegendWrapper
        ref={ref}
        className={className}
        verticalAlign={verticalAlign}
        align={align}
      >
        {payload.map((item) => {
          const key = `${nameKey || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const seriesKey = `${item.dataKey ?? item.value}`;

          // Resolve theme token names, and fall back to the CSS variable ChartStyle
          // emits so a config entry without an explicit colour still gets a swatch.
          // Keyed on the series, not `key`: a consumer-supplied `nameKey` is the same
          // string for every entry, which would paint the whole legend one colour.
          const swatchColour = itemConfig?.color
            ? getColorFromTheme(itemConfig.color, theme)
            : `var(--color-${seriesKey})`;

          const contents = (
            <>
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <ChartLegendSquareIcon color={swatchColour} />
              )}
              <ChartLegendLabel maxLabelWidth={maxLabelWidth}>{itemConfig?.label}</ChartLegendLabel>
            </>
          );

          if (!onToggleSeries) {
            return <ChartLegendElement key={seriesKey}>{contents}</ChartLegendElement>;
          }

          // `inactive` is recharts reporting the series' own `hide` prop, so a series
          // the consumer hides itself still reads as hidden.
          const isHidden = hiddenSeries?.has(seriesKey) || item.inactive === true;

          return (
            <ChartLegendButton
              key={seriesKey}
              type="button"
              aria-pressed={!isHidden}
              onClick={() => onToggleSeries(seriesKey)}
            >
              {contents}
            </ChartLegendButton>
          );
        })}
      </ChartLegendWrapper>
    );
  },
);
ChartLegendContent.displayName = "ChartLegend";

const ChartLegendWrapper = styled.div<{
  verticalAlign: RechartsPrimitive.LegendProps["verticalAlign"];
  align?: string;
}>(({ verticalAlign, align = "center" }) => ({
  display: "flex",
  alignItems: "start",
  justifyContent: align,
  gap: "1rem",
  // Ten buyer names don't fit on one row. Inert below the overflow point, so the
  // existing two-entry legends are unaffected.
  flexWrap: "wrap",
  rowGap: "0.5rem",
  ...(verticalAlign === "top"
    ? {
        paddingBottom: "0.75rem",
      }
    : {
        paddingTop: "0.75rem",
      }),
}));

const ChartLegendElement = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.375rem",

  "& > svg": {
    height: "0.75rem",
    width: "0.75rem",
    color: theme.colors.sysTextPlaceholder, // text-muted-foreground
  },
}));

// Keeps ChartLegendElement's styles verbatim so the enabled state looks identical
// to the non-interactive legend. The dimmed state is driven off aria-pressed rather
// than a prop, so the styling can't drift from what screen readers announce.
const ChartLegendButton = styled(ChartLegendElement.withComponent("button"))(({ theme }) => ({
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  color: "inherit",
  cursor: "pointer",

  '&[aria-pressed="false"]': {
    opacity: 0.4,
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimaryDefault}`,
    outlineOffset: "2px",
    borderRadius: "2px",
  },
}));

// The label needs its own element to truncate in: text-overflow does nothing on
// ChartLegendElement, which is a flex container. The label would be an anonymous
// flex item and would clip mid-word with no ellipsis.
const ChartLegendLabel = styled.span<{ maxLabelWidth?: number | string }>(({ maxLabelWidth }) =>
  maxLabelWidth === undefined
    ? {}
    : {
        maxWidth: maxLabelWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
);

const ChartLegendSquareIcon = styled.div<{ color?: string }>(({ color }) => ({
  height: "0.5rem",
  width: "0.5rem",
  flexShrink: 0,
  borderRadius: "2px",
  backgroundColor: color,
}));

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload =
    "payload" in payload && typeof payload.payload === "object" && payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (key in payload && typeof payload[key as keyof typeof payload] === "string") {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string;
  }

  return configLabelKey in config ? config[configLabelKey] : config[key as keyof typeof config];
}

export { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent };
